import {
  Menu as MuiMenu,
  MenuProps as MuiMenuProps,
  ListItemIcon,
  ListItemText,
  Divider,
  styled,
  Theme,
  Box,
} from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import { FormControlLabel } from '@components/formControlLabel';
import { Checkbox } from '@components/checkbox';
import { SyntheticEvent } from 'react';
import { MenuItem } from './MenuItem';

export enum MenuItemType {
  Default = 'default',
  Checkbox = 'checkbox',
  Divider = 'divider',
  Download = 'download',
  Icon = 'icon',
  Link = 'link',
}

export interface IMenuItem {
  type: MenuItemType;
  text?: string;
  icon?: React.ReactNode;
  checked?: boolean;
  disabled?: boolean;
  hidden?: boolean;
  actionName?: string;
  downloadUrl?: string;
  action?: (param?: any) => void;
  handleOnClick?: (param?: any) => void; // TODO: refactor this to action, but right now things are passing in action which will all 'turn on' if i use it
  isDisabled?: (param?: any) => boolean;
}

export interface IMenuItemExtended<T> extends IMenuItem {
  /** Handler for deciding if the item should be disabled */
  isDisabled?: (obj: T) => boolean;
  /** Handler for deciding if the item should be visible */
  isShown?: (obj: T) => boolean | undefined;
}

type MenuProps = MuiMenuProps & {
  items: IMenuItem[];
  onClickMenuItem: (index: number, innerText?: string, event?: SyntheticEvent) => void;
};

const StyledMenu = styled(MuiMenu)<MuiMenuProps>(({ theme }) => ({
  '& .MuiPaper-root': {
    color: 'black',
    padding: '16px 0',
    backgroundColor: (theme: Theme) => (theme.palette.theme === 'lio' ? 'common.white' : theme.palette.button.outlined),
    boxShadow: theme.palette.mode === 'light' ? 'boxShadow.3' : '0px 8px 32px rgba(0, 0, 0, 0.25);', // TDOO: add box shadows to theme
  },
}));

export function Menu({ items, onClickMenuItem, ...other }: MenuProps) {
  return (
    <StyledMenu
      sx={{
        '& .MuiList-root': { paddingBottom: '0px', paddingTop: '0px' },
        '& .MuiPaper-root': { marginTop: '5px' },
      }}
      {...other}
    >
      {items.map((menuItem: IMenuItem, index: number) => {
        if (menuItem.hidden) return null;
        if (menuItem.type === 'divider') {
          const key = `divider-${index}`;
          return (
            <Divider
              key={key}
              sx={{
                borderBottomWidth: '1px',
                borderColor: ({ palette }: Theme) => `${palette.border.main} !important`,
              }}
            />
          );
        }

        if (menuItem.type === 'download') {
          return (
            <MenuItem
              component='a'
              download
              target='_blank'
              rel='noopener'
              key={menuItem.text}
              disabled={menuItem.disabled}
              onClick={(e) => e.stopPropagation()}
              sx={{ textDecoration: 'none' }}
              {...({ href: menuItem.downloadUrl } as any)}
            >
              {menuItem.icon && (
                <>
                  <ListItemIcon>{menuItem.icon}</ListItemIcon>
                  <ListItemText>{menuItem.text}</ListItemText>
                </>
              )}
              {!menuItem.icon && <ListItemText>{menuItem.text}</ListItemText>}
            </MenuItem>
          );
        }

        return (
          <MenuItem
            data-testid={menuItem.text}
            onClick={(e) =>
              menuItem.handleOnClick ? menuItem.handleOnClick() : onClickMenuItem(index, menuItem.text, e)
            }
            key={menuItem.text}
            disabled={menuItem.disabled}
          >
            {menuItem.icon && (
              <>
                <ListItemIcon>{menuItem.icon}</ListItemIcon>
                <ListItemText>{menuItem.text}</ListItemText>
              </>
            )}
            {menuItem.type === 'checkbox' && (
              <FormControlLabel
                control={<Checkbox checked={menuItem.checked} sx={{ background: 'inherit' }} />}
                label={menuItem.text ? menuItem.text : ''}
                onClick={(e) => e.preventDefault()}
              />
            )}

            {menuItem.type === MenuItemType.Link && (
              <Box component={RouterLink} sx={{ textDecoration: 'none' }} to={menuItem.actionName ?? '/'}>
                <ListItemText>{menuItem.text}</ListItemText>
              </Box>
            )}

            {menuItem.type === 'default' && menuItem.text}
          </MenuItem>
        );
      })}
    </StyledMenu>
  );
}
